/*!
 * referral-ref.js — 前段サイトから受け取った紹介コード(ref)を Shopify のカート属性に書き込む
 *
 * 用途:
 *   前段サイト(合言葉で入るアフィリエイト用サイト)が商品ページへ
 *   `/products/{handle}?_ref=ATH01` の形で送り出す。この値を保存し、カートに商品が
 *   入ったタイミングで `POST /cart/update.js` により `attributes._ref` として書き込む。
 *   注文には `note_attributes` として残るので、ref 別に集計してキックバックを算出できる。
 *
 *   あわせて商品フォームに `properties[_ref]` を仕込む。「今すぐ購入する」(動的チェックアウト)は
 *   **カートを経由しない**ため、カート属性だけでは紐付けが落ちるため。
 *
 * 設置:
 *   1. このファイルを Shopify テーマの `assets/referral-ref.js` に置く
 *   2. `theme.liquid` の </body> 直前に次の1行を追加する
 *        {{ 'referral-ref.js' | asset_url | script_tag }}
 *   3. AJAX カートのテーマでは、カート追加が成功した直後に
 *        window.SomelifeReferral.apply();
 *      を呼ぶ(ページ遷移が起きないため、読み込み時の自動実行だけでは間に合わない)
 *
 * 設計上の約束:
 *   - 空のカートには書き込まない(Shopify 側で保持されないことがあるため)
 *   - 既に同じ値が入っていれば何もしない(冪等。毎ページ実行しても無害)
 *   - 失敗は握りつぶす(紐付けの失敗より購入の失敗のほうが損失が大きい)
 *   - _ref は顧客向け表示には出ないが、URL には残るため秘密情報ではない
 *
 * 詳細仕様: ../EC構成_野村氏構想.md の §7
 */
(function (window, document) {
  "use strict";

  // ============================================================
  // 設定
  // ============================================================
  var CONFIG = {
    // 前段サイトが付けてくるクエリパラメータ名。
    // 表記ゆれで取りこぼさないよう、先頭から順に探して最初に見つかったものを使う。
    paramNames: ["_ref", "ref"],
    // カート属性のキー名(注文の note_attributes に出る名前)。
    // アンダースコア始まりにすると、Shopify の顧客向けテンプレートが表示対象から外す。
    // 集計には管理画面の注文詳細・CSVエクスポート・Admin API から参照できる。
    attributeKey: "_ref",
    // 商品フォームに仕込む line item property のキー名。
    // 「今すぐ購入する」(動的チェックアウト)は**カートを経由しない**ため、
    // カート属性だけでは紐付けが落ちる。フォームに載せておけば購入経路を問わず残る。
    propertyKey: "_ref",
    // 保存キー(localStorage / Cookie 共通)
    storageKey: "somelife_ref",
    // アトリビューション期間(日)。これを過ぎた ref は無視する
    ttlDays: 30,
    // 同じ端末で別の ref を持って再来訪したときの扱い
    //   "last"  … 後から来た ref で上書きする(既定)
    //   "first" … 最初に来た ref を保持し続ける
    overwrite: "last",
    // 着地後にアドレスバーから ?ref= を消すか(履歴を汚さない)
    cleanUrl: true,
    // 動作確認時に true にすると console にログを出す
    debug: false,
  };

  var TTL_MS = CONFIG.ttlDays * 24 * 60 * 60 * 1000;

  function log() {
    if (!CONFIG.debug || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift("[referral-ref]");
    window.console.log.apply(window.console, args);
  }

  // ============================================================
  // 保存(localStorage が使えない環境では Cookie にフォールバック)
  // ============================================================
  function writeStore(value) {
    var json = JSON.stringify(value);
    try {
      window.localStorage.setItem(CONFIG.storageKey, json);
      return;
    } catch (e) {
      // Safari のプライベートブラウズ等で例外になる
    }
    try {
      var expires = new Date(Date.now() + TTL_MS).toUTCString();
      document.cookie =
        CONFIG.storageKey +
        "=" +
        encodeURIComponent(json) +
        "; expires=" +
        expires +
        "; path=/; SameSite=Lax";
    } catch (e2) {
      log("保存に失敗", e2);
    }
  }

  function readStore() {
    var raw = null;
    try {
      raw = window.localStorage.getItem(CONFIG.storageKey);
    } catch (e) {
      // 後段の Cookie を見る
    }
    if (!raw) {
      var m = document.cookie.match(
        new RegExp("(?:^|; )" + CONFIG.storageKey + "=([^;]*)"),
      );
      if (m) raw = decodeURIComponent(m[1]);
    }
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.ref !== "string") return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  /** 保存済みの ref。未保存・期限切れなら null。 */
  function getRef() {
    var stored = readStore();
    if (!stored) return null;
    if (typeof stored.at !== "number" || Date.now() - stored.at > TTL_MS) {
      log("期限切れのため無視", stored);
      return null;
    }
    return stored.ref;
  }

  /** 保存を消す(検証用)。 */
  function clearRef() {
    try {
      window.localStorage.removeItem(CONFIG.storageKey);
    } catch (e) {}
    document.cookie =
      CONFIG.storageKey + "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  }

  // ============================================================
  // URL から ref を取り込む
  // ============================================================
  function captureFromUrl() {
    var incoming = null;
    var params = null;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    for (var i = 0; i < CONFIG.paramNames.length; i++) {
      var v = params.get(CONFIG.paramNames[i]);
      if (v) {
        incoming = v;
        break;
      }
    }
    if (!incoming) return;

    // 想定外の値(長すぎる・記号混じり)は無視する。注文データに残るため保守的に。
    if (!/^[A-Za-z0-9_-]{1,32}$/.test(incoming)) {
      log("形式が不正なため無視", incoming);
      return;
    }

    var existing = getRef();
    if (existing && CONFIG.overwrite === "first") {
      log("既存の ref を維持", existing);
    } else {
      writeStore({ ref: incoming, at: Date.now() });
      log("保存", incoming);
    }

    if (CONFIG.cleanUrl && window.history && window.history.replaceState) {
      try {
        var url = new URL(window.location.href);
        for (var j = 0; j < CONFIG.paramNames.length; j++) {
          url.searchParams.delete(CONFIG.paramNames[j]);
        }
        window.history.replaceState({}, "", url.toString());
      } catch (e) {
        // URL API が無い環境では何もしない(実害なし)
      }
    }
  }

  // ============================================================
  // 商品フォームへ仕込む(「今すぐ購入する」対策)
  // ============================================================
  /**
   * /cart/add を送る全フォームに hidden input を差し込む。
   *
   * 動的チェックアウトボタン(「今すぐ購入する」)はカートを作らずにチェックアウトへ進むため、
   * カート属性では拾えない。フォームの line item property なら購入経路を問わず注文に残る。
   * キーがアンダースコア始まりなので、顧客向けの表示には出ない。
   */
  function injectFormProperty() {
    var ref = getRef();
    if (!ref) return;

    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      var name = "properties[" + CONFIG.propertyKey + "]";
      var input = form.querySelector('input[name="' + name + '"]');
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        form.appendChild(input);
      }
      if (input.value !== ref) input.value = ref;
    }
    log("商品フォームに property を仕込んだ", forms.length + "件");
  }

  // ============================================================
  // カートへ書き込む
  // ============================================================
  function apply() {
    var ref = getRef();
    if (!ref) {
      log("ref なし。何もしない");
      return Promise.resolve(false);
    }

    return fetch("/cart.js", { credentials: "same-origin" })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        // 空カートに属性だけ書いても保持されないことがある。商品が入ってから書く。
        if (!cart || cart.item_count === 0) {
          log("カートが空。まだ書き込まない");
          return false;
        }
        var current = cart.attributes && cart.attributes[CONFIG.attributeKey];
        if (current === ref) {
          log("既に同じ ref が入っている");
          return false;
        }
        var attributes = {};
        attributes[CONFIG.attributeKey] = ref;
        return fetch("/cart/update.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: attributes }),
        }).then(function () {
          log("カートに書き込んだ", ref);
          return true;
        });
      })
      .catch(function (e) {
        // 紐付けの失敗で購入を妨げない
        log("書き込みに失敗(購入は継続)", e);
        return false;
      });
  }

  // ============================================================
  // 初期化
  // ============================================================
  captureFromUrl();

  function onReady() {
    injectFormProperty();
    apply();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }

  // テーマが発火する代表的なカート更新イベントにも追随する(存在しなければ無害)。
  ["cart:updated", "cart:refresh", "cart:change"].forEach(function (name) {
    document.addEventListener(name, function () {
      apply();
    });
  });

  // AJAX カートのテーマから明示的に呼ぶための公開 API
  window.SomelifeReferral = {
    apply: apply,
    injectFormProperty: injectFormProperty,
    get: getRef,
    clear: clearRef,
    config: CONFIG,
  };
})(window, document);
